import { useAtomValue, useSetAtom } from "jotai";
import { v4 as uuidv4 } from "uuid";

import { NewGuestFormProps } from "../../../../../../../../features/AddPatrons/types";
import { currentProfileAtom } from "../../../../../../../../features/Club/atoms";
import { addPrivatePlayerAtom } from "../../../atoms/actions/addPlayer/addPlayerAtom";
import { replacePlayerOnRoundAtom } from "../../../atoms/actions/replacePlayerOnRoundAtom/replacePlayerOnRoundAtom";
import { swapOrReplacePlayerOnRoundAtom } from "../../../atoms/actions/swapPlayersBetweenGroupsAtom/swapPlayersBetweenGroupsAtom";
import {
  currentBookingAtom,
  currentModalStepAtom,
} from "../../../atoms/stores";

type Props = {
  courseLayout: {
    id: string;
    code: string;
    name: string;
  };
  enterEditableRoundsPrivateStep: () => void;
};

export const useNewGuestSubmission = ({
  courseLayout,
  enterEditableRoundsPrivateStep,
}: Props) => {
  const currentProfile = useAtomValue(currentProfileAtom);
  const addPlayerToBooking = useSetAtom(addPrivatePlayerAtom);
  const replacePlayerOnRound = useSetAtom(replacePlayerOnRoundAtom);
  const swapOrReplacePlayerOnRound = useSetAtom(swapOrReplacePlayerOnRoundAtom);
  const currentModalStep = useAtomValue(currentModalStepAtom);
  const currentBooking = useAtomValue(currentBookingAtom);

  const courseLayoutFromBookingData =
    currentBooking?.type === "Golf" ? courseLayout : undefined;

  const handlePlayerSubmission = (
    values: NewGuestFormProps,
    onFinish?: () => void
  ) => {
    const nameSplit = values.fullName.split(" ");
    const firstName = nameSplit.shift() || "";
    const lastName = nameSplit.join(" ") || "";

    const replacingRound =
      currentModalStep.type === "SwapPlayersPrivate"
        ? currentBooking?.rounds.find(
            (round) => round.localId === currentModalStep.roundId
          )
        : undefined;
    const hostRounds = replacingRound
      ? currentBooking?.rounds.filter(
          (round) =>
            round.localId !== replacingRound.localId &&
            (round.bookingIndex ?? 0) === (replacingRound.bookingIndex ?? 0) &&
            (round.player.clientType === "member" ||
              round.player.clientType === "customer")
        )
      : currentBooking?.rounds;
    const isHostInPlayerList = hostRounds?.some(
      (round) => round.player.id === currentProfile?.memberId
    );

    const newGuestPlayer = {
      id: `Guest:${uuidv4()}`,
      firstName,
      lastName,
      clientType: "newGuest" as const,
      hostId:
        isHostInPlayerList && currentProfile?.memberId
          ? currentProfile.memberId
          : hostRounds?.[0]?.player?.id ?? "",
      courseLayoutCode: courseLayoutFromBookingData?.code || null,
      courseLayoutId: courseLayoutFromBookingData?.id || null,
      courseLayoutName: courseLayout?.name || null,
      paymentStatus: "AUTO_CHARGE" as const,
      transportationType: undefined,
      memberGroups: [],
      profileImagePublicId: null,
    };

    if (currentModalStep.type === "SwapPlayersPrivate") {
      const changed = swapOrReplacePlayerOnRound({
        player: newGuestPlayer,
        roundId: currentModalStep.roundId,
      });
      if (!changed) return;
    } else if (currentModalStep.type === "ReplacePlayerPrivate") {
      replacePlayerOnRound({
        player: newGuestPlayer,
        roundId: currentModalStep.roundId,
      });
    } else {
      addPlayerToBooking({
        player: newGuestPlayer,
        bookingIndex:
          currentModalStep.type === "AddPlayerPrivate"
            ? currentModalStep.bookingIndex
            : undefined,
      });
    }

    enterEditableRoundsPrivateStep();
    if (onFinish) onFinish();
  };

  return handlePlayerSubmission;
};
